// CollapsibleFilters — shared primitive for hiding a list page's
// search/secondary filters behind a single Filters toggle.
//
// CONVENTION: list/table pages collapse their search + secondary filters
// behind this toggle, closed by default on desktop AND mobile, so the
// header stays compact. Adopt it via `useFilterDisclosure` (state) +
// `FilterToggleButton` (the toggle, inside `ToolbarActions`) + `FilterPanel`
// (the collapsible region). It mirrors `ToolbarActions` conventions.
import { useCallback, useId, useState, type CSSProperties, type ReactNode } from 'react';
import { Button } from './Button.js';
import type { IconName } from './Icon.js';

export interface UseFilterDisclosureResult {
  open: boolean;
  toggle: () => void;
  setOpen: (v: boolean) => void;
  panelId: string;
}

/**
 * @aiPurpose Disclosure state for a list page's collapsible filter region.
 * Owns the open/closed flag and a stable `panelId` (from `useId()`) so the
 * toggle's `aria-controls` and the panel's `id` always match.
 *
 * CONVENTION: list pages hide their search/secondary filters behind this
 * toggle, closed by default on desktop AND mobile (`initialOpen` defaults
 * to `false`).
 */
export function useFilterDisclosure(initialOpen = false): UseFilterDisclosureResult {
  const [open, setOpen] = useState(initialOpen);
  const panelId = useId();
  const toggle = useCallback(() => setOpen((v) => !v), []);
  return { open, toggle, setOpen, panelId };
}

export interface FilterToggleButtonProps {
  open: boolean;
  activeCount?: number;
  label: string;
  icon?: IconName;
  size?: 'sm' | 'md';
  'aria-controls': string;
  onClick: () => void;
  'data-testid'?: string;
}

/**
 * @aiPurpose The Filters toggle that opens/closes a `FilterPanel`. Mirrors
 * the existing list-header toggle look so adopting pages stay consistent: it
 * goes primary when the panel is open OR any filter is active, and appends an
 * active count to the label.
 *
 * INVARIANT: the `kind` flip plus the ` (n)` count suffix ARE the active
 * indicator — a filter that is active while the panel is closed must never be
 * invisible. Place inside `ToolbarActions` alongside the other header actions.
 */
export function FilterToggleButton({
  open,
  activeCount,
  label,
  icon = 'filter',
  size = 'sm',
  'aria-controls': ariaControls,
  onClick,
  'data-testid': dataTestid,
}: FilterToggleButtonProps) {
  const count = activeCount ?? 0;
  return (
    <Button
      kind={open || count > 0 ? 'primary' : 'secondary'}
      icon={icon}
      size={size}
      aria-expanded={open}
      aria-controls={ariaControls}
      onClick={onClick}
      {...(dataTestid !== undefined ? { 'data-testid': dataTestid } : {})}
    >
      {label + (count > 0 ? ` (${count})` : '')}
    </Button>
  );
}

export interface FilterPanelProps {
  open: boolean;
  id: string;
  children: ReactNode;
  style?: CSSProperties;
}

/**
 * @aiPurpose The collapsible region holding a list page's search/secondary
 * filter controls, toggled by `FilterToggleButton` via a shared id.
 *
 * INVARIANT: the container carrying `id` is ALWAYS mounted (hidden via
 * `display: 'none'` when closed, never unmounted) so the toggle's
 * `aria-controls` always resolves and children keep their input state/focus
 * across toggles. Caller `style` merges OVER the base for all properties.
 * `display` is ALWAYS forced last: closed panels are ALWAYS `display:'none'`
 * regardless of caller `style`; a caller-supplied `display` applies only when
 * the panel is open (falling back to `'block'` when none is provided).
 */
export function FilterPanel({ open, id, children, style }: FilterPanelProps) {
  const base: CSSProperties = {
    padding: '10px 24px',
    background: 'var(--bg-1)',
    borderBottom: '1px solid var(--border-1)',
  };
  return (
    <div
      id={id}
      style={{ ...base, ...style, display: open ? (style?.display ?? 'block') : 'none' }}
    >
      {children}
    </div>
  );
}
